import React, { useEffect, useState } from "react";
import { TrendingUp, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "../lib/api";

/**
 * FT-4 rate drift — the amber flag in RA, before the schedule slips.
 *
 * actual minutes per unit (approved hours ÷ installed countable units) against the rate the
 * schedule was built on. Amber at +20%, rose at +40%, and nothing at all until there are 40
 * approved hours and 15% of the scope installed. It flags; it never moves a date.
 */
export default function RateDriftBar({ projectId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    api.get(`/scheduling/projects/${projectId}/rate-drift`)
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [projectId]);

  if (!data?.enabled || !data.scopes?.length) return null;
  const worst = data.scopes.some((s) => s.drift_level === "over") ? "over" : "warn";
  const tone = worst === "over"
    ? "border-rose-300 bg-rose-50 text-rose-900"
    : "border-amber-300 bg-amber-50 text-amber-900";

  return (
    <div className={`rounded-lg border ${tone}`} data-testid="rate-drift-bar">
      <button type="button" onClick={() => setOpen((v) => !v)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left"
              data-testid="rate-drift-toggle">
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <TrendingUp className="h-4 w-4" />
        <span className="text-sm font-semibold">
          {data.scopes.length} scope{data.scopes.length === 1 ? "" : "s"} running over the planned
          pace
        </span>
        <span className="ml-auto text-[11px] opacity-70">
          amber at +{Math.round(data.amber_percent * 100)}% · rose at +{Math.round(data.red_percent * 100)}%
        </span>
      </button>
      {open && (
        <div className="space-y-1.5 px-3 pb-2.5">
          {data.scopes.map((s) => (
            <div key={s.scope_type} className="flex flex-wrap items-baseline gap-2 text-xs"
                 data-testid={`rate-drift-${s.scope_code || s.scope_type}`}>
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                s.drift_level === "over" ? "bg-rose-600 text-white" : "bg-amber-500 text-white"}`}>
                +{Math.round((s.drift || 0) * 100)}%
              </span>
              <span>{s.drift_message}</span>
              <span className="opacity-60">
                ({s.approved_hours} approved h · {Math.round((s.install_percent || 0) * 100)}% installed)
              </span>
            </div>
          ))}
          <p className="text-[11px] opacity-70">
            Nothing here has changed a date. Fix the pace, move the crew, or set the scope's rate by
            hand on the Info tab — whichever is true.
          </p>
        </div>
      )}
    </div>
  );
}
